/* import __COLOCATED_TEMPLATE__ from './selection-modal.hbs.js'; */
var _dec, _class, _descriptor;
function _initializerDefineProperty(e, i, r, l) { r && Object.defineProperty(e, i, { enumerable: r.enumerable, configurable: r.configurable, writable: r.writable, value: r.initializer ? r.initializer.call(l) : void 0 }); }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _applyDecoratedDescriptor(i, e, r, n, l) { var a = {}; return Object.keys(n).forEach(function (i) { a[i] = n[i]; }), a.enumerable = !!a.enumerable, a.configurable = !!a.configurable, ("value" in a || a.initializer) && (a.writable = !0), a = r.slice().reverse().reduce(function (r, n) { return n(i, e, r) || r; }, a), l && void 0 !== a.initializer && (a.value = a.initializer ? a.initializer.call(l) : void 0, a.initializer = void 0), void 0 === a.initializer ? (Object.defineProperty(i, e, a), null) : a; }
function _initializerWarningHelper(r, e) { throw Error("Decorating class property failed. Please ensure that transform-class-properties is enabled and runs after the decorators transform."); }
import { action, computed } from '@ember/object';
import Component from '@ember/component';
import { inject as service } from '@ember/service';
import { isBlank } from '@ember/utils';

/**

 ## Overview

 Modal that allows for selecting a subset of objects from a list.  It provides options
 for "Select All" and "Select None". On closing the modal, it will either return `null`,
 if the user cancelled or clicked outside the modal, or an array of the options marked
 as being selected or not. It will not modify any of its arguments.

 ## Usage

 ```javascript
 modal: Ember.inject.service(),

 actions: {
  editLocations() {
      const options = this.get('allUnits')
        .sortBy('nickname')
        .map(unit => ({
          checked: this.get('employee.units').includes(unit),
          value: unit,
          label: unit.get('nickname')
        }));

      this.get('modal').open('modals/selection-modal', {
        title: t('subunit.edit'),
        options
      })
        .then(savedOptions => {
          if (!Ember.isNone(savedOptions)) {
            this.get('employee.units').setObjects(savedOptions.filterBy('checked').mapBy('value'));
          }
        });
    },
}
```

@param {String} [title] - The title for the modal
@param {String} [subHeading] - The subheading for the modal
@param {String} [subText] - The subtext for the modal
@param {Array} [options] - Array of objects to select from
@param {Boolean} [enableSearch=true] - Adds UI for filtering the options via a search input
@param {String} [searchPlaceholder="Search"] - Optional placeholder text for search input
@param {String} [noResultsText="No results found for ‘{{term}}’"] - Text to display when searching yields no results. A default is used in the template if none is provided.
@param {Boolean} [enableSelectAll=true] - Enables Select All global checkbox
@param {Boolean} [enableIndeterminateState=false] - Enables the indeterminate state on the select all checkbox
@param {String} [selectAllLabel="Select All"] - The label for the Select All checkbox
@param {String} [primaryButtonLabel="Save"] - The label for the primary button
@param {String} [tertiaryColumnLabel] - The label for the tertiary data column

# Option Attributes
@param {Object} [value] - This object will be included with the returned options
@param {String} [label] - The label that will be displayed on the checkbox
@param {String} [labelSecondary] - The secondary label for the checkbox
@param {String} [labelTertiary] - A label that will appear to the far right of the checkbox
@param {Boolean} [isSelected] - Whether the checkbox should start out checked
@param {Boolean} [isDisabled] - Whether the checkbox should be disabled
@param {Boolean} [useLeadingAccessory] - Whether there is a leading accessory
@param {String} [leadingAccessoryImageUrl] - Image url to use for the leading accessory
@param {String} [leadingAccessorySvgName] - Svg name to use for the leading accessory
*/
let SelectionModal = (_dec = computed('initialIsSelectedValues', 'options.@each.checked'), _class = class SelectionModal extends Component {
  constructor(...args) {
    super(...args);
    _initializerDefineProperty(this, "intl", _descriptor, this);
    // passed in modalOptions
    _defineProperty(this, "title", null);
    _defineProperty(this, "options", null);
    _defineProperty(this, "enableSearch", true);
    _defineProperty(this, "searchPlaceholder", this.intl.t('common.search.placeholder'));
    _defineProperty(this, "noResultsText", null);
    _defineProperty(this, "enableSelectAll", true);
    _defineProperty(this, "enableIndeterminateState", false);
    _defineProperty(this, "selectAllLabel", this.intl.t('common.selectAll'));
    _defineProperty(this, "primaryButtonLabel", this.intl.t('button.save'));
    _defineProperty(this, "initialIsSelectedValues", null);
    _defineProperty(this, "filterQuery", null);
  }
  get filterProperties() {
    return ['label', 'labelSecondary', 'labelTertiary'];
  }
  get isClean() {
    return this.options.every((option, i) => {
      return option.checked === this.initialIsSelectedValues[i];
    });
  }
  didReceiveAttrs() {
    super.didReceiveAttrs();
    const modalOptions = this.modalOptions;
    const options = modalOptions.options;
    this.setProperties(Object.assign(modalOptions, {
      initialIsSelectedValues: options.map(option => option.checked)
    }));
  }
  save() {
    const initialIsSelectedValues = this.initialIsSelectedValues;
    const results = this.options.map((option, i) => {
      const didChange = option.checked !== initialIsSelectedValues[i];
      return {
        ...option,
        didChange
      };
    });
    this.close(results);
  }
  unlockModalHeightIfQueryEmpty() {
    if (isBlank(this.filterQuery)) {
      this.unlockModalHeight();
    }
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "intl", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class.prototype, "filterProperties", [computed], Object.getOwnPropertyDescriptor(_class.prototype, "filterProperties"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "isClean", [_dec], Object.getOwnPropertyDescriptor(_class.prototype, "isClean"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "save", [action], Object.getOwnPropertyDescriptor(_class.prototype, "save"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "unlockModalHeightIfQueryEmpty", [action], Object.getOwnPropertyDescriptor(_class.prototype, "unlockModalHeightIfQueryEmpty"), _class.prototype), _class);
export { SelectionModal as default };